import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { TYPE_LABELS } from '@/utils/movie';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = movies.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 5500);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (dir: number) => setIndex((v) => (v + dir + count) % count);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[460px] w-full md:h-[560px]">
        {movies.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            <img
              src={movie.backdrop || movie.poster}
              alt={`${movie.title} 焦点推荐`}
              title={`${movie.title} - 丝瓜视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30" />
          </div>
        ))}

        <div className="relative mx-auto flex h-full w-full max-w-[1360px] items-center px-4 md:px-6">
          <div className="w-full max-w-xl">
            {movies.map((movie, i) => (
              <div key={movie.id} className={i === index ? 'block animate-fade-up' : 'hidden'}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 font-label text-xs font-semibold text-background-50">
                  <i className="ri-fire-fill" /> 焦点推荐
                </span>
                <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
                  {movie.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-100">
                  <span className="flex items-center gap-1 font-semibold text-accent-400">
                    <i className="ri-star-fill" /> {movie.rating.toFixed(1)}
                  </span>
                  <span>{TYPE_LABELS[movie.type]}</span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span className="hidden sm:inline">{movie.genres.join(' / ')}</span>
                </div>
                <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-100/90 md:line-clamp-3 md:text-base">
                  {movie.synopsis}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${movie.id}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-lg" /> 立即观看
                  </Link>
                  <Link
                    to={`/category/${movie.type}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 font-label text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                  >
                    <i className="ri-compass-3-line text-base" /> 更多同类
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-5 right-4 flex items-center gap-3 md:right-6">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-background-50 backdrop-blur transition-colors hover:bg-background-50/25"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <div className="flex items-center gap-1.5">
            {movies.map((movie, i) => (
              <button
                key={movie.id}
                type="button"
                aria-label={`切换到 ${movie.title}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/60'}`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-background-50 backdrop-blur transition-colors hover:bg-background-50/25"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}